// Helpers for translating text that doubles as data.
//
// Airtable field names (libCon.ATF_*) and Airtable cell values are english
// strings that index records and drive comparisons. They must keep their
// english identity everywhere except on screen, so instead of translating the
// constants these helpers derive a catalogue key from the english text and
// translate only at render time.
import * as libCon from '../community-hats-js-library/Constants.js';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from '../community-hats-js-library/utils/generalFunctions.js';
import { translationExists, getCurrentI18n } from '../hooks/LanguageHooks';

/**
 * 'Days Until Expire' -> 'fieldDaysUntilExpire'
 * 'Ready for Revision' -> 'valueReadyForRevision'
 */
export const toTranslationKey = (prefix, english) => {
  if (english === null || english === undefined) return null;

  const words = `${english}`
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 0);

  if (words.length === 0) return null;

  return prefix + words.map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join('');
};

const translateWithPrefix = (i18n, prefix, english) => {
  const key = toTranslationKey(prefix, english);

  // A key that was never added to en.json falls back to the english text, so a
  // forgotten translation reads exactly as the dashboard did before.
  if (key === null || !translationExists(i18n, key)) return english;

  return i18n.t(key);
};

/** Translates an Airtable field name for display (table headers, labels). */
export const tField = (i18n, english) => translateWithPrefix(i18n, 'field', english);

/**
 * Translates an Airtable cell value for display (tags, cells). Never for
 * comparison. A multiple select field arrives as an array, which is mapped
 * rather than joined so it renders exactly as it did before.
 */
export const tValue = (i18n, english) =>
  Array.isArray(english)
    ? english.map((v) => translateWithPrefix(i18n, 'value', v))
    : translateWithPrefix(i18n, 'value', english);

// The Issues table keeps its translations in sibling columns named after the
// english field plus a locale suffix ('Description Hi'), english being the
// unsuffixed one. Free text authored per record cannot be keyed off its own
// content the way tValue does, so the language picks the column instead of the
// catalogue entry.
const LOCALIZED_FIELDS = {
  [libCon.ATF_DESCRIPTION]: {
    [libCon.ES]: libCon.ATF_DESCRIPTION_ES,
    [libCon.HI]: libCon.ATF_DESCRIPTION_HI,
    [libCon.GU]: libCon.ATF_DESCRIPTION_GU,
  },
  [libCon.ATF_ACTION]: {
    [libCon.ES]: libCon.ATF_ACTION_ES,
    [libCon.HI]: libCon.ATF_ACTION_HI,
    [libCon.GU]: libCon.ATF_ACTION_GU,
  },
};

/**
 * Reads an Airtable field in the current language. English has no suffixed
 * column and resolves to the field itself, and a row nobody translated yet
 * (airtable omits the empty cell) falls back to it as well, so a missing
 * translation reads exactly as the dashboard did before.
 */
export const tRecordField = (i18n, record, field) => {
  if (isNullOrUndefined(record)) return undefined;

  const byLocale = LOCALIZED_FIELDS[field];
  const localizedField = byLocale === undefined ? undefined : byLocale[i18n.locale];
  const localized = localizedField === undefined ? undefined : record[localizedField];

  return isNullOrUndefinedOrEmpty(localized) ? record[field] : localized;
};

// The library composes multi device tooltips as "Label: message" lines and
// hardcodes the label in english -- the translator it accepts is applied to the
// message half only. These are every label it can emit; the sensor lists use a
// brand and serial instead, which is not translatable and falls through.
const COMPOSED_LABELS = [
  libCon.DEVICE_SENSORS,
  libCon.DEVICE_PHONE,
  libCon.DEVICE_WEARABLE,
  libCon.ATF_ISSUES,
  'Participant Intake Form',
  'House Intake Form',
];

const translateComposedLabels = (i18n, text) =>
  text
    .split('\n')
    .map((line) => {
      const label = COMPOSED_LABELS.find((l) => line.startsWith(`${l}: `));
      return label === undefined ? line : `${tValue(i18n, label)}${line.slice(label.length)}`;
    })
    .join('\n');

/**
 * Translates a value that may already be a catalogue key (a libCon.MESSAGE_* or
 * an error status). Anything else is a sentence the library already composed,
 * where only the hardcoded labels are left to translate.
 */
export const tMessage = (i18n, value) => {
  if (translationExists(i18n, value)) return i18n.t(value);

  return typeof value === 'string' ? translateComposedLabels(i18n, value) : value;
};

/** tMessage for the code that runs outside of a component. */
export const translateMessage = (value) => tMessage(getCurrentI18n(), value);
